// src/components/modals/upsert-event-v2/UpsertEventModal.jsx
import { useEffect, useState } from "react";
import ModalV2 from "../../ModalV2";
import useModal from "../../../hooks/useModal";
import UpsertEventDetailsStep from "./UpsertEventDetailsStep";
import UpsertEventPricingStep from "./UpsertEventPricingStep";
import { Button } from "@oc-labs/wt-storybook";
import toast from "react-hot-toast";
import useEvent from "../../../hooks/useEvent";
import useMultimedia from "../../../hooks/useMultimedia";

// initial values
const emptyEventParams = {
  id: undefined,
  name: "",
  coverImage: null,
  galleryImages: [],
  location: "",
  startDate: "",
  startTime: "",
  endDate: "",
  endTime: "",
  maxAttendees: 0,
  description: "",
  price: 0,
  currency: "CHF",
  discountPrice: 0,
  discountPercentage: 0,
  contractRequiredToAttend: [],
  contractRequiredToDiscount: [],
  isPaid: false,
};

function buildDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const time = timeStr || "00:00";
  const [hours, minutes] = time.split(":").map(Number);
  const d = new Date(dateStr);
  if (Number.isFinite(hours) && Number.isFinite(minutes)) {
    d.setHours(hours, minutes, 0, 0);
  }
  return d;
}

export default function UpsertEventModal({ reload }) {
  const { modal, close } = useModal("upsert-event");
  const { upload } = useMultimedia();
  const { upsertEvent } = useEvent();
  const [event, setEvent] = useState(emptyEventParams);
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isFile = (v) => typeof File !== "undefined" && v instanceof File;
  const isDataUrl = (v) => typeof v === "string" && v.startsWith("data:image/");
  const dataUrlToFile = (dataUrl, filename = `image-${Date.now()}.png`) => {
    const [meta, b64] = (dataUrl || "").split(",");
    const match = /data:(.*);base64/.exec(meta || "");
    const mime = match?.[1] || "image/png";
    const bin = typeof atob === "function" ? atob(b64 || "") : "";
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  };

  const toUrl = async (v) => {
    if (isFile(v)) return await upload(v);
    if (isDataUrl(v)) return await upload(dataUrlToFile(v));
    return (typeof v === "string" && v.trim()) ? v : null;
  };

  useEffect(() => {
    if (modal?.props) {
      const newEventProps = { ...modal.props };
      const toDateInput = (iso) => iso ? new Date(iso).toISOString().split("T")[0] : "";
      const toTimeInput = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        const offset = d.getTimezoneOffset();
        d.setMinutes(d.getMinutes() - offset);
        return d.toISOString().slice(11, 16);
      };
      if (newEventProps.startDate) {
        newEventProps.startTime = toTimeInput(newEventProps.startDate);
        newEventProps.startDate = toDateInput(newEventProps.startDate);
      }
      if (newEventProps.endDate) {
        newEventProps.endTime = toTimeInput(newEventProps.endDate);
        newEventProps.endDate = toDateInput(newEventProps.endDate);
      }
      setEvent((prev) => ({ ...prev, ...newEventProps }));
    }
  }, [modal?.props]);

  useEffect(() => {
    if (!modal?.isOpen) {
      setEvent(emptyEventParams);
      setStep(0);
    }
  }, [modal?.isOpen]);

  const startDateTime = buildDateTime(event?.startDate, event?.startTime);
  const endDateTime = buildDateTime(event?.endDate, event?.endTime);
  const now = new Date();
  const isStartInPast = !!startDateTime && startDateTime < now;
  const isEndBeforeStart = !!startDateTime && !!endDateTime && endDateTime < startDateTime;
  const hasDateErrors = isStartInPast || isEndBeforeStart;

  // Steps: 0 = Cover/Organizer, 1 = Dates/Times, 2 = Info, 3 = Gallery, 4 = Pricing (if paid)
  const detailsStepsCount = 4;
  const isPaid = !!event.isPaid;
  const isPricingStep = isPaid && step === detailsStepsCount;
  const isSubmitStep = (!isPaid && step === detailsStepsCount - 1) || (isPaid && step === detailsStepsCount);
  const isSubmitDisabled = hasDateErrors;

  const submit = async () => {
    setIsSubmitting(true);
    const combineDateTime = (date, time) => {
      if (!date) return null;
      const d = new Date(date);
      if (time) {
        const [hours, minutes] = time.split(":").map(Number);
        d.setHours(hours, minutes, 0, 0);
      }
      return d.toISOString();
    };
    const coverUrl = await toUrl(event.coverImage);
    const uploaded = await Promise.all((event.galleryImages || []).map(toUrl));
    const cleanGallery = uploaded.filter((u) => typeof u === "string" && u.trim());
    const payload = {
      ...event,
      coverImage: coverUrl || undefined,
      galleryImages: cleanGallery,
      startDate: combineDateTime(event.startDate, event.startTime),
      endDate: combineDateTime(event.endDate, event.endTime),
    };
    delete payload.startTime;
    delete payload.endTime;
    try {
      await upsertEvent(payload);
      toast.success("Event submitted");
      if (reload) await reload();
      close();
    } catch (err) {
      toast.error(err?.message || "Failed to submit event");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (isSubmitStep) {
      submit();
    } else {
      setStep((prev) => prev + 1);
    }
  };
  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  // Stepper labels
  const stepLabels = [
    "Cover & Organizer",
    "Dates & Times",
    "Info",
    "Gallery",
    ...(isPaid ? ["Pricing"] : []),
  ];

  return (
    <ModalV2 name={"upsert-event"} className="w-[600px] h-[70vh] overflow-y-auto">
      <div className="flex flex-col h-full">
        {/* Stepper */}
        {/* Stepper mejorado visualmente */}
        <div className="flex flex-col items-center mt-2 w-full">
          <div className="flex items-center w-full justify-center">
            {stepLabels.map((label, idx) => (
              <div key={label} className="flex flex-col items-center w-1/5">
                <div className={`transition-all duration-300 shadow ${step === idx ? 'bg-blue-600 text-white scale-110' : 'bg-gray-200 text-gray-700'} rounded-full w-8 h-8 flex items-center justify-center font-semibold text-base mb-1`}>
                  {idx + 1}
                </div>
                <div className={`text-xs text-center ${step === idx ? 'font-bold text-blue-600' : 'text-gray-500'}`}>{label}</div>
              </div>
            ))}
          </div>
          {/* Barra de progreso */}
          <div className="w-5/5 h-2 bg-gray-200 rounded-full mt-4 relative">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / stepLabels.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Steps navigation y botones fijos */}
        <div className="flex flex-col flex-grow relative min-h-[400px]">
          {/* Contenido scrollable */}
          <div className="flex-grow overflow-y-auto">
            {step < detailsStepsCount && (
              <div className="mt-2">
                <UpsertEventDetailsStep
                  event={event}
                  setEvent={setEvent}
                  step={step}
                  onNext={handleNext}
                  onBack={handleBack}
                />
              </div>
            )}
            {isPricingStep && (
              <div className="p-6 mb-4">
                <UpsertEventPricingStep event={event} setEvent={setEvent} />
              </div>
            )}
          </div>
          {/* Botones fijos abajo */}
          <div className="sticky bottom-0 left-0 w-full bg-white pt-2 pb-4 z-10 flex gap-4 px-6">
            {step > 0 && (
              <Button type="secondary" className="w-full" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button className="w-full" onClick={handleNext} disabled={isSubmitDisabled || isSubmitting}>
              {isSubmitStep ? (isSubmitting ? "Creating event..." : "Send") : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </ModalV2>
  );
}